import { CreateTicketModalTitle } from "~/pages/tickets/components/CreateTicketDialog/CreateTicketModalTitle/CreateTicketModalTitle";
import { ReactNode, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Link,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "~/components/loading-button";
import { useSnackbar } from "notistack";
import { useJiraTicketContext } from "~/hooks/useJiraTicketContext";
import {
  ClientIntegrationType,
  CreateCaseInput,
  ServiceNowTicketConfigInput,
  TicketRefType,
  useGetCaseContentQuery,
} from "~/operations";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  CaseTarget,
  SelectedEntityWithTicketData,
} from "~/pages/tickets/types";
import { Space } from "~/lib/types";
import { CreateTicketJiraDialog } from "~/pages/tickets/components/CreateTicketDialog/CreateTicketJiraDialog";
import { CreateTicketEmailDialog } from "~/pages/tickets/components/CreateTicketDialog/CreateTicketEmailDialog";
import { IntegrationDynamicIcon } from "~/pages/integrations/components/IntegrationDynamicIcon";
import { Flex } from "~/components/Flex";
import { useEmailTicketContext } from "~/hooks/useEmailTicketContext";
import { useTicketIntegrations } from "~/pages/tickets/ticket/hooks/useTicketIntegrations";
import { CreateTicketZendeskDialog } from "~/pages/tickets/components/CreateTicketDialog/CreatTicketZendeskDialog/CreateTicketZendeskDialog";
import { CreateTicketGithubDialog } from "~/pages/tickets/components/CreateTicketDialog/CreateTicketGithubDialog";
import { CreateTicketGitlabDialog } from "~/pages/tickets/components/CreateTicketDialog/CreateTicketGitlabDialog";
import { useAzureTicketContext } from "~/hooks/useAzureTicketContext";
import { CreateTicketAzureDialog } from "~/pages/tickets/components/CreateTicketDialog/CreateTicketAzureDialog";
import { CreateTicketWebhookDialog } from "~/pages/tickets/components/CreateTicketDialog/CreateTicketWebhookDialog";
import { CreateTicketServiceNowDialog } from "~/pages/tickets/components/CreateTicketDialog/CreateTicketServiceNowDialog";
import { ScopeType, SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { useZendeskTicketContext } from "~/hooks/useZendeskTicketContext";
import { docsHref } from "~/lib/docs-href";
import { DialogProps } from "@toolpad/core";
import { useStepper } from "~/hooks/useStepper";

export type CreateCasesFormInput = {
  jira: CreateCasesJiraFormInput;
  email: CreateCasesEmailFormInput;
  zendesk: CreateCasesZendeskFormInput;
  github: CreateCasesGithubIssuesFormInput;
  gitlab: CreateCasesGitlabIssuesFormInput;
  azure: CreateCasesAzureDevOpsFormInput;
  webhook: CreateCasesWebhookFormInput;
  servicenow: CreateCasesServiceNowFormInput;
};

const createCaseFormDefaultValues: CreateCasesFormInput = {
  jira: {
    title: "",
    projectId: "",
    notes: "",
  },
  email: {
    title: "",
    recipient: "",
    notes: "",
  },
  zendesk: {
    title: "",
    priority: "",
    zendeskType: "",
    notes: "",
  },
  github: {
    title: "",
    repo: "",
    owner: "",
    notes: "",
  },
  gitlab: {
    title: "",
    project: "",
    notes: "",
  },
  azure: {
    title: "",
    projectId: "",
    notes: "",
  },
  webhook: {
    url: "",
    title: "",
    notes: "",
  },
  servicenow: {
    title: "",
    notes: "",
    impact: undefined,
    priority: undefined,
    urgency: undefined,
  },
};

type CreateCasesJiraFormInput = {
  title: string;
  projectId: string;
  notes: string;
};

type CreateCasesEmailFormInput = {
  recipient: string;
  title: string;
  notes: string;
};

export type CreateCasesZendeskFormInput = {
  title: string;
  priority: string;
  zendeskType: string;
  notes: string;
};

export type CreateCasesGithubIssuesFormInput = {
  title: string;
  owner: string;
  repo: string;
  notes: string;
};

export type CreateCasesGitlabIssuesFormInput = {
  title: string;
  project: string;
  notes: string;
};

type CreateCasesAzureDevOpsFormInput = {
  title: string;
  projectId: string;
  notes: string;
};

export type CreateCasesWebhookFormInput = {
  url: string;
  title: string;
  notes: string;
};

export type CreateCasesServiceNowFormInput = {
  title: string;
  notes: string;
  impact?: number;
  priority?: number;
  urgency?: number;
};

export interface CreateTicketDialogPayload {
  onSave: (params: CreateCaseInput) => Promise<void>;
  loading: boolean;
  target: CaseTarget;
  isCombinedTicket: boolean;
  selectedEntities: SelectedEntityWithTicketData[];
  space: Space;
  scope: SpaceOrWorkspaceScope;
  hasCreateCasePermissions: boolean;
  assetsCount?: number;
  isInitiative?: boolean;
}

export const CreateTicketDialog = ({
  onClose,
  open,
  payload,
}: DialogProps<CreateTicketDialogPayload>) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    onSave,
    loading,
    selectedEntities,
    space,
    scope,
    hasCreateCasePermissions,
    assetsCount,
    isCombinedTicket,
    isInitiative = false,
  } = payload;

  const isMultipleFindingsSelected = selectedEntities.length > 1;
  const { activeStep, canGoNext, handleNext } = useStepper({
    steps: selectedEntities.length,
  });

  const initiativeRefs = isInitiative
    ? selectedEntities.map((r) => ({
        entityMrn: r.scopeMrn,
        initiativeMrn: r.mrn,
      }))
    : [];

  const references = isInitiative
    ? []
    : isCombinedTicket && isMultipleFindingsSelected
      ? selectedEntities.map((r) => ({
          findingMrn: r.mrn,
          scopeMrn: String(r.scopeMrn),
        }))
      : [
          {
            findingMrn: selectedEntities[activeStep].mrn,
            scopeMrn: String(selectedEntities[activeStep].scopeMrn),
          },
        ];

  const {
    selectedIntegration,
    ticketingIntegrations,
    selectedIntegrationMrn,
    setSelectedIntegrationMrn,
    saveSelectedIntegration,
  } = useTicketIntegrations({
    spaceMrn: space.mrn,
  });

  const {
    saveSelectedProject: saveSelectedJiraProject,
    loadSelectedProject: loadSelectedJiraProject,
    projects: jiraProjects,
    isProjectsEmpty: isJiraProjectsEmpty,
    fetchProjects: fetchJiraProjects,
  } = useJiraTicketContext({
    scopeMrn: selectedIntegration?.mrn || "",
    skip:
      !open ||
      !selectedIntegration?.mrn ||
      selectedIntegration?.type !== ClientIntegrationType.TicketSystemJira,
  });

  const { recipients, isRecipientsEmpty } = useEmailTicketContext({
    scopeMrn: selectedIntegration?.mrn || "",
    skip:
      !open ||
      !selectedIntegration?.mrn ||
      selectedIntegration?.type !== ClientIntegrationType.TicketSystemEmail,
  });

  const {
    saveSelectedProject: saveSelectedAzureProject,
    loadSelectedProject: loadSelectedAzureProject,
    projects: azureProjects,
    isProjectsEmpty: isAzureProjectsEmpty,
    fetchProjects: fetchAzureProjects,
  } = useAzureTicketContext({
    scopeMrn: selectedIntegration?.mrn || "",
    skip:
      !open ||
      !selectedIntegration?.mrn ||
      selectedIntegration?.type !==
        ClientIntegrationType.TicketSystemAzureDevops,
  });

  const { zendeskFields, zendeskLoading } = useZendeskTicketContext({
    scopeMrn: selectedIntegration?.mrn || "",
    skip:
      !open ||
      !selectedIntegration?.mrn ||
      selectedIntegration?.type !== ClientIntegrationType.TicketSystemZendesk,
  });

  const { data: jiraCaseContentData, loading: jiraCaseContentLoading } =
    useGetCaseContentQuery({
      variables: {
        input: {
          type: TicketRefType.Jira,
          references,
        },
      },
      skip:
        !open ||
        selectedIntegration?.type !== ClientIntegrationType.TicketSystemJira,
    });

  const { data: emailCaseContentData, loading: emailCaseContentLoading } =
    useGetCaseContentQuery({
      variables: {
        input: {
          type: TicketRefType.Email,
          references,
        },
      },
      skip:
        !open ||
        selectedIntegration?.type !== ClientIntegrationType.TicketSystemEmail,
    });

  const { data: zendeskCaseContentData, loading: zendeskCaseContentLoading } =
    useGetCaseContentQuery({
      variables: {
        input: {
          type: TicketRefType.Zendesk,
          references,
        },
      },
      skip:
        !open ||
        selectedIntegration?.type !== ClientIntegrationType.TicketSystemZendesk,
    });

  const { data: githubCaseContentData, loading: githubCaseContentLoading } =
    useGetCaseContentQuery({
      variables: {
        input: {
          type: TicketRefType.Github,
          references,
        },
      },
      skip:
        !open ||
        selectedIntegration?.type !== ClientIntegrationType.TicketSystemGithub,
    });

  const { data: gitlabCaseContentData, loading: gitlabCaseContentLoading } =
    useGetCaseContentQuery({
      variables: {
        input: {
          type: TicketRefType.Gitlab,
          references,
        },
      },
      skip:
        !open ||
        selectedIntegration?.type !== ClientIntegrationType.TicketSystemGitlab,
    });

  const { data: azureCaseContentData, loading: azureCaseContentLoading } =
    useGetCaseContentQuery({
      variables: {
        input: {
          type: TicketRefType.AzureDevops,
          references,
        },
      },
      skip:
        !open ||
        selectedIntegration?.type !==
          ClientIntegrationType.TicketSystemAzureDevops,
    });

  const { data: webhookCaseContentData, loading: webhookCaseContentLoading } =
    useGetCaseContentQuery({
      variables: {
        input: {
          type: TicketRefType.Email, // TODO: change to webhook when we have a webhook typeref
          references,
        },
      },
      skip:
        !open ||
        selectedIntegration?.type !== ClientIntegrationType.TicketSystemWebhook,
    });

  const {
    data: servicenowCaseContentData,
    loading: servicenowCaseContentLoading,
  } = useGetCaseContentQuery({
    variables: {
      input: {
        type: TicketRefType.ServiceNow,
        references,
      },
    },
    skip:
      !open ||
      selectedIntegration?.type !==
        ClientIntegrationType.TicketSystemServiceNow,
  });

  const {
    register,
    handleSubmit,
    formState,
    reset,
    control,
    getValues,
    getFieldState,
  } = useForm<CreateCasesFormInput>({
    mode: "onChange",
    defaultValues: {
      jira: createCaseFormDefaultValues.jira,
      email: createCaseFormDefaultValues.email,
      zendesk: createCaseFormDefaultValues.zendesk,
      github: createCaseFormDefaultValues.github,
      gitlab: createCaseFormDefaultValues.gitlab,
      azure: createCaseFormDefaultValues.azure,
      webhook: createCaseFormDefaultValues.webhook,
      servicenow: createCaseFormDefaultValues.servicenow,
    },
  });

  useEffect(() => {
    const savedSelectedProject = loadSelectedJiraProject();

    reset({
      ...getValues(),
      jira: {
        title: jiraCaseContentData?.caseContent?.title,
        projectId: savedSelectedProject ? savedSelectedProject.key : "",
        notes: "",
      },
    });
  }, [jiraCaseContentData, jiraProjects]);

  useEffect(() => {
    reset({
      ...getValues(),
      email: {
        recipient: recipients?.length === 1 ? recipients[0].email : "",
        title: emailCaseContentData?.caseContent.title,
        notes: "",
      },
    });
  }, [emailCaseContentData, selectedIntegrationMrn, recipients]);

  useEffect(() => {
    reset({
      ...getValues(),
      zendesk: {
        title: zendeskCaseContentData?.caseContent.title,
        priority: zendeskFields?.priority?.[0]?.value || "",
        zendeskType: zendeskFields?.type?.[0]?.value || "",
        notes: "",
      },
    });
  }, [zendeskCaseContentData, selectedIntegrationMrn, zendeskFields]);

  useEffect(() => {
    if (
      selectedIntegration?.configurationOptions?.__typename !==
      "GithubTicketingConfigurationOptions"
    )
      return;

    reset({
      ...getValues(),
      github: {
        owner:
          selectedIntegration?.configurationOptions?.defaultRepoOwner || "",
        repo: selectedIntegration?.configurationOptions?.defaultRepoName || "",
        title: githubCaseContentData?.caseContent.title,
        notes: "",
      },
    });
  }, [githubCaseContentData, selectedIntegrationMrn, selectedIntegration]);

  useEffect(() => {
    if (
      selectedIntegration?.configurationOptions?.__typename !==
      "GitlabTicketingConfigurationOptions"
    )
      return;

    reset({
      ...getValues(),
      gitlab: {
        project:
          selectedIntegration?.configurationOptions?.gitlabDefaultProject || "",
        title: gitlabCaseContentData?.caseContent.title,
        notes: "",
      },
    });
  }, [gitlabCaseContentData, selectedIntegrationMrn, selectedIntegration]);

  useEffect(() => {
    const savedSelectedProject = loadSelectedAzureProject();

    reset({
      ...getValues(),
      azure: {
        title: azureCaseContentData?.caseContent?.title,
        projectId: savedSelectedProject ? savedSelectedProject.name : "",
        notes: "",
      },
    });
  }, [azureCaseContentData, azureProjects]);

  useEffect(() => {
    reset({
      ...getValues(),
      webhook: {
        url: "",
        title: webhookCaseContentData?.caseContent?.title,
        notes: "",
      },
    });
  }, [webhookCaseContentData]);

  useEffect(() => {
    reset({
      ...getValues(),
      servicenow: {
        title: servicenowCaseContentData?.caseContent?.title,
        notes: "",
      },
    });
  }, [servicenowCaseContentData]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit: SubmitHandler<CreateCasesFormInput> = async (
    formData,
    event,
  ) => {
    const { jira, email, zendesk, github, gitlab, azure, webhook, servicenow } =
      formData;
    setIsSubmitting(true);

    // When calling createCase the caller MUST always set the isV2 argument to true.
    // That is because initiatives contain >1 finding and will never work with ticketing v1.
    const baseCaseInput = {
      workspaceMrn: scope.type === ScopeType.Workspace ? scope.mrn : undefined,
      references,
      initiativeRefs,
      ...(isInitiative && { isV2: true }),
    };

    try {
      switch (selectedIntegration?.type) {
        case ClientIntegrationType.TicketSystemJira:
          return await onSave({
            ...baseCaseInput,
            ticketConfig: {
              jira: {
                projectKey: jira.projectId,
                issueType: "Task",
              },
            },
            title: jira.title,
            notes: jira.notes,
            integrationMrn: selectedIntegration?.mrn || "",
          });
        case ClientIntegrationType.TicketSystemEmail:
          return await onSave({
            ...baseCaseInput,
            ticketConfig: {
              email: {
                emailAddress: email.recipient,
              },
            },
            title: email.title,
            notes: email.notes,
            integrationMrn: selectedIntegration?.mrn || "",
          });
        case ClientIntegrationType.TicketSystemZendesk:
          return await onSave({
            ...baseCaseInput,
            ticketConfig: {
              zendesk: {
                priority: zendesk.priority,
                type: zendesk.zendeskType,
              },
            },
            title: zendesk.title,
            notes: zendesk.notes,
            integrationMrn: selectedIntegration?.mrn || "",
          });
        case ClientIntegrationType.TicketSystemGithub:
          return await onSave({
            ...baseCaseInput,
            ticketConfig: {
              github: {
                owner: github.owner,
                repo: github.repo,
              },
            },
            title: github.title,
            notes: github.notes,
            integrationMrn: selectedIntegration?.mrn || "",
          });

        case ClientIntegrationType.TicketSystemGitlab:
          return await onSave({
            ...baseCaseInput,
            ticketConfig: {
              gitlab: {
                project: gitlab.project,
              },
            },
            title: gitlab.title,
            notes: gitlab.notes,
            integrationMrn: selectedIntegration?.mrn || "",
          });

        case ClientIntegrationType.TicketSystemAzureDevops:
          return await onSave({
            ...baseCaseInput,
            ticketConfig: {
              azureDevops: {
                projectName: azure.projectId,
              },
            },
            title: azure.title,
            notes: azure.notes,
            integrationMrn: selectedIntegration?.mrn || "",
          });
        case ClientIntegrationType.TicketSystemWebhook:
          return await onSave({
            ...baseCaseInput,
            ticketConfig: {},
            title: webhook.title,
            notes: webhook.notes,
            integrationMrn: selectedIntegration?.mrn || "",
          });
        case ClientIntegrationType.TicketSystemServiceNow:
          const serviceNowConfig: ServiceNowTicketConfigInput = {
            ...(servicenow.impact !== undefined && {
              impact: servicenow.impact,
            }),
            ...(servicenow.priority !== undefined && {
              priority: servicenow.priority,
            }),
            ...(servicenow.urgency !== undefined && {
              urgency: servicenow.urgency,
            }),
          };

          return await onSave({
            ...baseCaseInput,
            ticketConfig: {
              serviceNow: serviceNowConfig,
            },
            title: servicenow.title,
            notes: servicenow.notes,
            integrationMrn: selectedIntegration?.mrn || "",
          });
      }
    } catch (error) {
      enqueueSnackbar(`Failed to create ticket`, {
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
      if (!isCombinedTicket && isMultipleFindingsSelected && canGoNext) {
        handleNext();
      } else {
        handleClose();
      }
    }
  };

  const selectIntegrationRender = (selected: ReactNode) => {
    const selectedRecipient = ticketingIntegrations?.find(
      (int) => int.value === selected,
    );

    if (!selectedRecipient) {
      return <>Select an integration</>;
    }

    const selectedRecipientTitle = (
      <Flex gap={1}>
        {selectedRecipient.type && (
          <IntegrationDynamicIcon type={selectedRecipient.type} />
        )}
        {selectedRecipient.label}
      </Flex>
    );

    if (ticketingIntegrations?.length === 0) {
      return <>No integrations available – create one first</>;
    }

    if (!selected || !selectedRecipientTitle) {
      return <>Select a recipient</>;
    }

    return <>{selectedRecipientTitle}</>;
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={handleClose}
      data-testid="create-ticket-dialog"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <CreateTicketModalTitle
          onClose={handleClose}
          assetsCount={assetsCount}
        />
        <DialogContent sx={{ pb: 5, px: 5 }}>
          {selectedIntegration?.type ===
            ClientIntegrationType.TicketSystemJira && (
            <Typography color="text.secondary" mb={3}>
              Mondoo shares the ticket with the external ticketing or email
              integration you choose. To learn more, read the{" "}
              <Link
                sx={{ color: "inherit", textDecoration: "underline" }}
                href={docsHref("/platform/ticketing/overview/")}
                target="_blank"
              >
                Mondoo documentation
              </Link>
              .
            </Typography>
          )}
          <Typography
            variant="body1"
            gutterBottom
            sx={{ mb: 1, mt: 1, fontWeight: 700 }}
          >
            Integration
          </Typography>
          <Select
            fullWidth
            disabled={!hasCreateCasePermissions}
            sx={{
              "&.MuiInputBase-root": {
                bgcolor: "code.background",
              },
            }}
            displayEmpty
            onChange={(e) => {
              saveSelectedIntegration(e.target.value);
              setSelectedIntegrationMrn(e.target.value);
            }}
            value={selectedIntegrationMrn}
            renderValue={selectIntegrationRender}
          >
            {ticketingIntegrations?.map((integration) => (
              <MenuItem value={integration.value} key={integration.value}>
                <Flex gap={1}>
                  {integration.type && (
                    <IntegrationDynamicIcon type={integration.type} />
                  )}
                  {integration.label}
                </Flex>
              </MenuItem>
            ))}
          </Select>
          <Box width={1} py={5}>
            <Divider />
          </Box>
          {selectedIntegration?.type ===
            ClientIntegrationType.TicketSystemJira && (
            <CreateTicketJiraDialog
              hasCreateCasePermissions={hasCreateCasePermissions}
              register={register}
              control={control}
              formState={formState}
              isProjectsEmpty={isJiraProjectsEmpty}
              projects={jiraProjects}
              saveSelectedProject={saveSelectedJiraProject}
              fetchProjects={(query: string) =>
                fetchJiraProjects(query, selectedIntegrationMrn)
              }
            />
          )}
          {selectedIntegration?.type ===
            ClientIntegrationType.TicketSystemEmail && (
            <CreateTicketEmailDialog
              hasCreateCasePermissions={hasCreateCasePermissions}
              register={register}
              control={control}
              formState={formState}
              isRecipientsEmpty={isRecipientsEmpty}
              recipients={recipients}
            />
          )}
          {selectedIntegration?.type ===
            ClientIntegrationType.TicketSystemZendesk && (
            <CreateTicketZendeskDialog
              hasCreateCasePermissions={hasCreateCasePermissions}
              register={register}
              formState={formState}
              fields={zendeskFields}
              loading={zendeskLoading}
            />
          )}
          {selectedIntegration?.type ===
            ClientIntegrationType.TicketSystemGithub && (
            <CreateTicketGithubDialog
              hasCreateCasePermissions={hasCreateCasePermissions}
              register={register}
              formState={formState}
              errors={formState.errors}
              control={control}
              getFieldState={getFieldState}
              disabled={false}
            />
          )}
          {selectedIntegration?.type ===
            ClientIntegrationType.TicketSystemGitlab && (
            <CreateTicketGitlabDialog
              hasCreateCasePermissions={hasCreateCasePermissions}
              register={register}
              formState={formState}
              errors={formState.errors}
              control={control}
              getFieldState={getFieldState}
              disabled={false}
            />
          )}
          {selectedIntegration?.type ===
            ClientIntegrationType.TicketSystemAzureDevops && (
            <CreateTicketAzureDialog
              hasCreateCasePermissions={hasCreateCasePermissions}
              register={register}
              control={control}
              formState={formState}
              isProjectsEmpty={isAzureProjectsEmpty}
              projects={azureProjects}
              saveSelectedProject={saveSelectedAzureProject}
              fetchProjects={(query: string) =>
                fetchAzureProjects(query, selectedIntegrationMrn)
              }
            />
          )}
          {selectedIntegration?.type ===
            ClientIntegrationType.TicketSystemWebhook && (
            <CreateTicketWebhookDialog
              hasCreateCasePermissions={hasCreateCasePermissions}
              register={register}
              formState={formState}
            />
          )}
          {selectedIntegration?.type ===
            ClientIntegrationType.TicketSystemServiceNow && (
            <CreateTicketServiceNowDialog
              hasCreateCasePermissions={hasCreateCasePermissions}
              register={register}
              formState={formState}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pt: 0, pb: 5, px: 5 }}>
          <Flex
            justifyContent={
              !isCombinedTicket && isMultipleFindingsSelected
                ? "space-between"
                : "center"
            }
            width="100%"
          >
            {!isCombinedTicket && isMultipleFindingsSelected && (
              <Typography>
                Ticket {activeStep + 1} of {selectedEntities.length}
              </Typography>
            )}
            <Stack direction="row" spacing={2} justifyContent="space-between">
              {
                <LoadingButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={
                    !formState.isValid ||
                    !hasCreateCasePermissions ||
                    !selectedIntegration ||
                    loading ||
                    jiraCaseContentLoading ||
                    emailCaseContentLoading ||
                    zendeskCaseContentLoading ||
                    githubCaseContentLoading ||
                    gitlabCaseContentLoading ||
                    azureCaseContentLoading ||
                    webhookCaseContentLoading ||
                    servicenowCaseContentLoading
                  }
                  loading={
                    isSubmitting ||
                    loading ||
                    jiraCaseContentLoading ||
                    emailCaseContentLoading ||
                    zendeskCaseContentLoading ||
                    githubCaseContentLoading ||
                    gitlabCaseContentLoading ||
                    azureCaseContentLoading ||
                    webhookCaseContentLoading ||
                    servicenowCaseContentLoading
                  }
                  buttonText={"Create"}
                  data-testid="modal-create-ticket-button"
                />
              }
            </Stack>
          </Flex>
        </DialogActions>
      </form>
    </Dialog>
  );
};
